import { useParams, Navigate, Link } from "react-router-dom";
import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { ProcessSteps, TechMarquee, FAQ } from "../components/Sections";
import { Contact } from "../components/Contact";
import { Reveal } from "../components/motion";
import { SERVICES, PROJECTS, FAQS, LEGAL_ENTITY } from "../data";
import { Check, ArrowRight, ExternalLink } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  if (service.canonicalSlug) return <Navigate to={`/services/${service.canonicalSlug}`} replace />;
  const Icon = service.icon;

  const relevantProjects = PROJECTS.filter((p) => {
    if (slug === "mobile-apps") return p.cat === "Mobile";
    if (slug === "web-development") return p.cat === "Web";
    if (slug === "software-development" || slug === "custom-software") {
      return ["buildhealer", "indusdeal", "pawsup"].includes(p.slug);
    }
    if (slug === "ai-solutions" || slug === "ai-development" || slug === "ai-automation") {
      return ["indusdeal", "foxrun", "pawsup"].includes(p.slug);
    }
    if (slug === "cloud-infrastructure" || slug === "cloud-devops" || slug === "cybersecurity") {
      return ["indusdeal", "buildhealer", "pawsup"].includes(p.slug);
    }
    if (slug === "iot-solutions" || slug === "iot") {
      return ["pawsup", "foxrun", "mycitydekho"].includes(p.slug);
    }
    if (slug === "erp" || slug === "hrms" || slug === "crm") {
      return ["indusdeal", "buildhealer", "mycitydekho"].includes(p.slug);
    }
    return ["buildhealer", "ecopro-builders", "indusdeal"].includes(p.slug);
  }).slice(0, 3);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.seoDesc || service.desc,
    "url": `https://neuravolt.in/services/${slug}`,
    "provider": {
      "@type": "Organization",
      "@id": "https://neuravolt.in/#organization",
      "name": "NeuraVolt Technology",
      "legalName": LEGAL_ENTITY,
      "url": "https://neuravolt.in"
    },
    "areaServed": [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Arab Emirates" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": service.title,
      "itemListElement": (service.features || []).map(f => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": f }
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://neuravolt.in/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://neuravolt.in/services" },
      { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://neuravolt.in/services/${slug}` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  const jsonLd = [serviceSchema, breadcrumbSchema, faqSchema];

  return (
    <Layout>
      <Seo
        title={service.seoTitle || `${service.title} Services | NeuraVolt`}
        description={service.seoDesc || `${service.desc} Professional ${service.title.toLowerCase()} services by NeuraVolt.`}
        keywords={service.seoKeywords || `${service.title}, ${service.title} services India, ${service.title} company, NeuraVolt ${service.title.toLowerCase()}`}
        path={`/services/${slug}`}
        jsonLd={jsonLd}
      />
      <PageHero eyebrow="Service" title={service.title} sub={service.desc} />

      <section className="py-24 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="w-14 h-14 rounded-2xl bg-[#FF5A1F]/10 grid place-items-center">
                <Icon className="w-7 h-7 text-[#FF5A1F]" />
              </span>
              {service.brandLogo && (
                <img src={service.brandLogo} alt="" className="h-8 sm:h-10 w-auto max-w-[160px] object-contain ml-2" />
              )}
            </div>
            <h2 className="font-display text-3xl font-bold text-[#0B0B0B] mb-4">Overview</h2>
            <p className="text-lg text-neutral-600 leading-relaxed mb-4">{service.desc}</p>
            {service.localProof && (
              <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/80 mb-6 text-sm text-neutral-600 flex items-start gap-3">
                <span className="inline-block w-2 h-2 rounded-full bg-[#FF5A1F] mt-1.5 flex-shrink-0" />
                <span><strong className="text-neutral-900 font-semibold">Delivery &amp; Infrastructure:</strong> {service.localProof}</span>
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors group">
                Discuss your project <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              {service.externalUrl && (
                <a
                  href={service.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 font-semibold transition-colors"
                >
                  <span>Visit {service.externalUrl.replace("https://", "")}</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="card-soft rounded-3xl p-8">
              <h3 className="font-heading text-xl font-semibold text-[#0B0B0B] mb-5">What's included</h3>
              <ul className="space-y-4">
                {service.features.map((f) => (
                  <li key={f} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#FF5A1F] grid place-items-center shrink-0"><Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /></span>
                    <span className="text-neutral-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Interlinked Proven Case Studies */}
      {relevantProjects.length > 0 && (
        <section className="py-20 px-5 sm:px-8 bg-[#0B0B0B] text-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-xs font-mono font-bold text-[#FF5A1F] uppercase tracking-wider block mb-2">
                  PROVEN CLIENT SUCCESS
                </span>
                <h2 className="text-2xl sm:text-4xl font-display font-bold">
                  Case Studies in {service.title}
                </h2>
              </div>
              <Link
                to="/portfolio"
                className="text-sm font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1.5 shrink-0"
              >
                <span>View all portfolio projects</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relevantProjects.map((p) => {
                const logoUrl = p.logo ? p.logo : (p.domain ? `https://www.google.com/s2/favicons?domain=${p.domain}&sz=128` : null);
                return (
                  <Link
                    key={p.slug}
                    to={`/portfolio/${p.slug}`}
                    className="rounded-2xl bg-white/5 border border-white/10 hover:border-[#FF5A1F]/40 p-5 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="relative aspect-[16/10] rounded-xl overflow-hidden mb-4">
                        <img
                          src={p.img}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-2.5 right-2.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#FF5A1F] text-white">
                          {p.cat}
                        </span>
                        {p.domain && (
                          <span className="absolute bottom-2.5 left-2.5 text-[10px] font-mono px-2 py-0.5 rounded bg-black/80 backdrop-blur text-white border border-white/20">
                            {p.domain}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt=""
                            className="w-5 h-5 rounded object-contain border border-white/20"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        <h3 className="font-heading text-lg font-bold text-white group-hover:text-[#FF5A1F] transition-colors">
                          {p.title}
                        </h3>
                      </div>

                      <p className="text-neutral-400 text-xs sm:text-sm line-clamp-2 leading-relaxed mb-4">
                        {p.desc}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                      <span className="text-neutral-400 font-mono text-[11px]">
                        {p.badge || "Engineered by Neuravolt"}
                      </span>
                      <span className="text-[#FF5A1F] font-semibold inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        Case Study <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ProcessSteps />
      <TechMarquee />
      <FAQ />
      <Contact defaultService={service.title} />
    </Layout>
  );
}
